import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { Theme } from '@material-ui/core'
import { makeStyles } from '@material-ui/core'
import IconButton from '@material-ui/core/IconButton'
import Menu from '@material-ui/core/Menu'
import MenuItem from '@material-ui/core/MenuItem'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import MoreHorizIcon from '@material-ui/icons/MoreHoriz'
import CheckBoxIcon from '@material-ui/icons/CheckBoxOutlined'
import CheckBoxFilledIcon from '@material-ui/icons/CheckBox'
import CloudUploadIcon from '@material-ui/icons/CloudUploadOutlined'
import InsertDriveFileOutlinedIcon from '@material-ui/icons/InsertDriveFileOutlined'
import PictureAsPdfOutlinedIcon from '@material-ui/icons/PictureAsPdfOutlined'
import CircularProgress from '@material-ui/core/CircularProgress'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'
import classnames from 'classnames'
import { useDropzone } from 'react-dropzone'
import type { QuoteFile } from 'paintscout'
import FileViewerButton from './FileViewerButton'
import CloudinaryImage from '../CloudinaryImage'
import { isImageFile, isPdfFile } from './file-type-utils'

export type FileViewerGalleryFile = QuoteFile & { error?: string }

export interface FileViewerGalleryProps {
  files: FileViewerGalleryFile[]
  selectedKey?: string
  onSelect: (file: FileViewerGalleryFile) => void
  onSelectAll?: () => void
  onUploadClick?: () => void
  /**
   * Called with files dropped onto the upload tile. Uploads them as if they
   * were picked via the file input.
   */
  onUploadFiles?: (files: File[]) => void
  /**
   * Multi-select state. When non-empty, the gallery enters multi-select mode:
   * thumbs render a checkbox overlay and clicks toggle selection instead of
   * navigating the preview.
   */
  selectedKeys?: Set<string>
  /**
   * Toggle a file's membership in the multi-select set. When omitted, the
   * gallery stays in single-preview mode regardless of `selectedKeys`.
   */
  onToggleSelect?: (file: FileViewerGalleryFile) => void
  /**
   * Width below which the gallery switches to single-column compact mode
   * and the "Select all" / "Upload" actions collapse into a kebab menu.
   * Ignored when `orientation === 'horizontal'`.
   */
  compactThreshold?: number
  /**
   * 'vertical' (default) renders a 2-column grid suitable for a sidebar.
   * 'horizontal' renders a single-row strip of thumbnails for mobile layouts.
   */
  orientation?: 'vertical' | 'horizontal'
}

const useStyles = makeStyles<Theme>((theme) => ({
  root: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    background: theme.palette.common.white
  },
  rootHorizontal: {
    height: 'auto'
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5, 2, 1, 2),
    minHeight: 44
  },
  title: {
    ...theme.typography.subtitle1,
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.paintscout.palette.brand.Neutral.P30
  },
  scroll: {
    flex: 1,
    overflowY: 'auto',
    // small top padding so the selected thumb's box-shadow ring isn't clipped on the first row
    padding: theme.spacing(0.25, 2, 2, 2)
  },
  scrollHorizontal: {
    flex: 'none',
    overflowX: 'auto',
    overflowY: 'hidden',
    padding: theme.spacing(0.25, 2, 1.5, 2)
  },
  grid: {
    display: 'grid',
    gap: theme.spacing(1),
    gridTemplateColumns: 'repeat(2, 1fr)'
  },
  gridCompact: {
    gridTemplateColumns: '1fr'
  },
  strip: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(1),
    justifyContent: 'flex-start'
  },
  headerActions: {
    display: 'inline-flex',
    gap: theme.spacing(1)
  },
  horizontalActions: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1, 2, 1.5, 2)
  },
  thumb: {
    position: 'relative',
    aspectRatio: '1 / 1',
    width: '100%',
    padding: 0,
    // iPad lifts a preview of the tapped element when the upload tile opens the
    // native file picker; suppress it (#20634).
    WebkitTapHighlightColor: 'transparent',
    WebkitTouchCallout: 'none',
    WebkitUserSelect: 'none',
    background: theme.paintscout.palette.brand.Neutral.P96,
    border: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
    borderRadius: theme.borderRadius.lg,
    cursor: 'pointer',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.paintscout.palette.brand.Neutral.P50,
    transition: 'border-color 120ms ease, box-shadow 120ms ease',
    '&:hover': {
      borderColor: theme.paintscout.palette.brand.Neutral.P70
    }
  },
  stripThumb: {
    flex: '0 0 72px',
    width: 72,
    minWidth: 72,
    height: 72,
    aspectRatio: 'unset'
  },
  thumbSelected: {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 1px ${theme.palette.primary.main}`
  },
  thumbImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block'
  },
  pdfPreviewSlot: {
    flex: '1 1 auto',
    minHeight: 0,
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    marginTop: theme.spacing(1),
    marginBottom: `-${theme.spacing(0.5)}px`,
    '& img': {
      maxWidth: '100%',
      maxHeight: '100%',
      objectFit: 'contain'
    }
  },
  pdfPreviewSlotHorizontal: {
    marginTop: 0,
    marginBottom: 0,
    padding: theme.spacing(0.5)
  },
  pdfTitle: {
    textAlign: 'left',
    color: theme.paintscout.palette.brand.Neutral.P50
  },
  pdfBadge: {
    alignSelf: 'flex-start',
    paddingLeft: theme.spacing(1),
    color: theme.paintscout.palette.brand.Neutral.P50
  },
  docTitle: {
    ...theme.typography.caption,
    color: theme.paintscout.palette.brand.Neutral.P30,
    padding: theme.spacing(0, 1),
    textAlign: 'center',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: '100%',
    marginTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5)
  },
  docFormat: {
    ...theme.typography.caption,
    color: theme.paintscout.palette.brand.Neutral.P50,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(0.5)
  },
  uploadTile: {
    border: `1px dashed ${theme.paintscout.palette.brand.Neutral.P85}`,
    background: theme.palette.common.white,
    color: theme.paintscout.palette.brand.Neutral.P50,
    '&:hover': {
      borderColor: theme.palette.primary.main,
      color: theme.palette.primary.main
    }
  },
  uploadTileDragActive: {
    borderStyle: 'solid',
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.main
  },
  uploadTileDragReject: {
    borderStyle: 'solid',
    borderColor: theme.palette.error.main,
    color: theme.palette.error.main,
    '&:hover': {
      borderColor: theme.palette.error.main,
      color: theme.palette.error.main
    }
  },
  uploadIcon: {
    fontSize: 28
  },
  uploadLabel: {
    ...theme.typography.caption,
    marginTop: theme.spacing(0.5)
  },
  menuItemIcon: {
    minWidth: 32
  },
  checkboxOverlay: {
    position: 'absolute',
    top: theme.spacing(0.75),
    right: theme.spacing(0.75),
    width: 22,
    height: 22,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 4,
    background: theme.palette.common.white,
    color: theme.palette.primary.main,
    pointerEvents: 'none',
    boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
    '& svg': {
      fontSize: 22
    }
  },
  spinnerOverlay: {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'rgba(255, 255, 255, 0.6)'
  },
  errorOverlay: {
    color: theme.palette.error.main
  }
}))

function FileViewerGallery(props: FileViewerGalleryProps) {
  const {
    files,
    selectedKey,
    onSelect,
    onSelectAll,
    onUploadClick,
    onUploadFiles,
    selectedKeys,
    onToggleSelect,
    compactThreshold = 180,
    orientation = 'vertical'
  } = props
  const classes = useStyles({})
  const rootRef = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState<number>(280)
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const isHorizontal = orientation === 'horizontal'
  const inMultiSelectMode = !!onToggleSelect && !!selectedKeys && selectedKeys.size > 0
  const canSelectAll = !!onSelectAll && files.length > 0

  useLayoutEffect(() => {
    if (!rootRef.current) return
    setWidth(rootRef.current.getBoundingClientRect().width)
  }, [])

  useEffect(() => {
    if (!rootRef.current || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (entry) setWidth(entry.contentRect.width)
    })
    ro.observe(rootRef.current)
    return () => ro.disconnect()
  }, [])

  const compact = !isHorizontal && width < compactThreshold

  const { getRootProps, isDragActive, isDragReject, draggedFiles } = useDropzone({
    onDrop: (accepted) => {
      if (accepted.length) onUploadFiles?.(accepted)
    },
    // matches the hidden file input in FileViewer
    accept: 'image/*,application/pdf',
    multiple: true,
    noClick: true, // click still goes through onUploadClick → hidden input
    noKeyboard: true,
    disabled: !onUploadFiles
  })
  // Chrome doesn't populate dragged file types during dragover, so isDragReject
  // alone would flag every drag as invalid
  const rejectDrag = isDragReject && draggedFiles.some((f) => f.type !== '')

  return (
    <div ref={rootRef} className={classnames(classes.root, isHorizontal && classes.rootHorizontal)}>
      <div className={classes.header}>
        <span className={classes.title}>Files</span>
        {isHorizontal ? null : compact ? (
          <>
            <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)} aria-label="File actions">
              <MoreHorizIcon fontSize="small" />
            </IconButton>
            <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
              {canSelectAll && (
                <MenuItem
                  onClick={() => {
                    onSelectAll?.()
                    setMenuAnchor(null)
                  }}
                >
                  <ListItemIcon className={classes.menuItemIcon}>
                    <CheckBoxIcon fontSize="small" />
                  </ListItemIcon>
                  Select all
                </MenuItem>
              )}
              {onUploadClick && (
                <MenuItem
                  onClick={() => {
                    onUploadClick()
                    setMenuAnchor(null)
                  }}
                >
                  <ListItemIcon className={classes.menuItemIcon}>
                    <CloudUploadIcon fontSize="small" />
                  </ListItemIcon>
                  Upload
                </MenuItem>
              )}
            </Menu>
          </>
        ) : (
          canSelectAll && (
            <FileViewerButton onClick={onSelectAll} tone="neutral">
              Select all
            </FileViewerButton>
          )
        )}
      </div>
      <div className={classnames(classes.scroll, isHorizontal && classes.scrollHorizontal)}>
        <div
          className={classnames(
            isHorizontal ? classes.strip : classes.grid,
            !isHorizontal && compact && classes.gridCompact
          )}
        >
          {files.map((file) => {
            const isImage = isImageFile(file)
            const isPdf = isPdfFile(file)
            const isMultiSelected = !!selectedKeys?.has(file.key)
            const selected = inMultiSelectMode ? isMultiSelected : file.key === selectedKey
            const uploading = file.isUploading
            const errored = !!file.error

            return (
              <button
                key={file.key}
                type="button"
                className={classnames(
                  classes.thumb,
                  isHorizontal && classes.stripThumb,
                  selected && classes.thumbSelected
                )}
                onClick={() => (inMultiSelectMode && onToggleSelect ? onToggleSelect(file) : onSelect(file))}
              >
                {inMultiSelectMode && isMultiSelected && (
                  <div className={classes.checkboxOverlay}>
                    <CheckBoxFilledIcon fontSize="small" />
                  </div>
                )}
                {isImage && file.src ? (
                  // crossOrigin keeps the cached response CORS-clean so the
                  // editor (which loads the same src with CORS) gets a usable
                  // cache hit instead of a poisoned non-CORS entry on Safari.
                  <img crossOrigin="anonymous" src={file.src} alt={file.title || ''} className={classes.thumbImg} />
                ) : isPdf ? (
                  <>
                    <div
                      className={classnames(classes.pdfPreviewSlot, isHorizontal && classes.pdfPreviewSlotHorizontal)}
                    >
                      {file.cloudinaryPublicId ? (
                        <CloudinaryImage
                          publicId={`${file.cloudinaryPublicId}.png`}
                          page={1}
                          resourceType="image"
                          crop="fit"
                          width={isHorizontal ? 50 : 64}
                        />
                      ) : (
                        <InsertDriveFileOutlinedIcon fontSize="large" />
                      )}
                    </div>
                    {!isHorizontal && (
                      <>
                        <div className={classnames(classes.docTitle, classes.pdfTitle)} title={file.title}>
                          {file.title || 'File'}
                        </div>
                        <div className={classnames(classes.docFormat, classes.pdfBadge)}>
                          <PictureAsPdfOutlinedIcon fontSize="small" />
                          PDF
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <InsertDriveFileOutlinedIcon fontSize="large" />
                    <div className={classes.docTitle} title={file.title}>
                      {file.title || 'File'}
                    </div>
                    {file.format && (
                      <div className={classes.docFormat}>{file.format.split('/').pop()?.toUpperCase()}</div>
                    )}
                  </>
                )}
                {uploading && (
                  <div className={classes.spinnerOverlay}>
                    <CircularProgress size={24} />
                  </div>
                )}
                {errored && !uploading && (
                  <div className={classnames(classes.spinnerOverlay, classes.errorOverlay)} title={file.error}>
                    <ErrorOutlineIcon />
                  </div>
                )}
              </button>
            )
          })}
          {onUploadClick && !isHorizontal && !compact && (
            <button
              type="button"
              {...getRootProps({ onClick: onUploadClick })}
              className={classnames(
                classes.thumb,
                classes.uploadTile,
                isDragActive && !rejectDrag && classes.uploadTileDragActive,
                rejectDrag && classes.uploadTileDragReject
              )}
            >
              <CloudUploadIcon className={classes.uploadIcon} />
              <span className={classes.uploadLabel}>
                {rejectDrag ? 'Invalid file type' : isDragActive ? 'Drop files here' : 'Upload a file'}
              </span>
            </button>
          )}
        </div>
      </div>
      {isHorizontal && (canSelectAll || onUploadClick) && (
        <div className={classes.horizontalActions}>
          {canSelectAll ? (
            <FileViewerButton onClick={onSelectAll} tone="neutral">
              <CheckBoxIcon fontSize="small" style={{ marginRight: 4 }} />
              Select all
            </FileViewerButton>
          ) : (
            <span />
          )}
          {onUploadClick && (
            <FileViewerButton onClick={onUploadClick} tone="neutral">
              <CloudUploadIcon fontSize="small" style={{ marginRight: 4 }} />
              Upload a file
            </FileViewerButton>
          )}
        </div>
      )}
    </div>
  )
}

export default FileViewerGallery
